import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { AudioPlayerItem, RadioSchedule, RadioStationBasicInfo } from '@/models/RadioTypes';
import { AddOn, AddOnWithQuantity } from '@/models/PackageTypes';
import { CartItem } from '@/models/ProductTypes';
import { Customer } from '@/models/ProductTypes';
import {
  validateAddress,
  validateEmail,
  validateFirstName,
  validateNationalId,
  validatePhoneNumber,
  validatePostalCode,
} from '../utils/validation';
import { SellableProduct } from '@/models/CatalogTypes';

const stationStorage = createJSONStorage<RadioStationBasicInfo | null>(() => sessionStorage);
const activeAudioStorage = createJSONStorage<AudioPlayerItem | null>(() => sessionStorage);

export const activeRadioAtom = atomWithStorage<RadioStationBasicInfo | null>('activeRadioAtom', null, stationStorage);
export const activeAudioAtom = atomWithStorage<AudioPlayerItem | null>('activeAudioAtom', null, activeAudioStorage);
export const isPlayingRadioAtom = atom<boolean>(false);
export const scheduleAtom = atom<Record<string, RadioSchedule[]>>({});

export const activeScheduleAtom = atom<RadioSchedule[]>((get) => {
  const activeRadio = get(activeRadioAtom);
  const schedule = get(scheduleAtom);
  return schedule[activeRadio?.id || ''] || [];
});

export const volumeAtom = atom<number>(100);
export const isMutedAtom = atom<boolean>(false);
export const fixedRadioPlayerAtom = atom<boolean>(false);
export const fullscreenBoxAtom = atom<boolean>(false);
export const isFullscreenExhaustedAtom = atom<boolean>(false);

export const isDoBusinessWithUsFormAtom = atom<boolean>(false);
export const isCallMeModalOpenAtom = atom<boolean>(false);
export const callMePrependTextAtom = atom<string>('');

export const packageIdAtom = atom<string | undefined>(undefined);
export const addOnsAtom = atom<Record<string, AddOnWithQuantity>>({});

export const appendAddOnsAtom = atom(null, (get, set, update: AddOn[]) => {
  const addOns = get(addOnsAtom);
  update.forEach((addOn) => {
    if (addOn.id) {
      addOns[addOn.id] = { addOn: addOn, quantity: 0 };
    }
  });
  set(addOnsAtom, addOns);
});

export const basePriceAtom = atom<number>(0);
export const packageTitleAtom = atom<string>('');

export const totalPriceAtom = atom<number>((get) => {
  const basePrice = get(basePriceAtom);
  const addOns = get(addOnsAtom);
  return Object.keys(addOns).reduce((total, id) => {
    const price = addOns[id].addOn.price ?? 0;
    return total + addOns[id].quantity * price;
  }, basePrice);
});

export const isPackageFormOpenAtom = atom(false);

export const addOnsQuantityAtom = atom(null, (get, set, update: { id: string; change: -1 | 1 }) => {
  const { id, change } = update;
  const addOns = get(addOnsAtom);
  const currentQuantity = addOns[id].quantity;
  const multiselect = addOns[id].addOn.multiselect;
  const newQuantity = currentQuantity + change;

  if (newQuantity >= 0 && (multiselect ? newQuantity <= 9 : newQuantity <= 1)) {
    set(addOnsAtom, {
      ...addOns,
      [id]: {
        ...addOns[id],
        quantity: newQuantity,
      },
    });
  }
});

export const setPackageAtom = atom(
  null,
  (_, set, update: { id: string | undefined; basePrice: number; title: string }) => {
    set(packageIdAtom, update.id);
    set(basePriceAtom, update.basePrice);
    set(packageTitleAtom, update.title);
  }
);

export const clearPackageAtom = atom(null, (_, set) => {
  set(packageIdAtom, undefined);
  set(addOnsAtom, {});
  set(basePriceAtom, 0);
  set(packageTitleAtom, '');
  set(isPackageFormOpenAtom, false);
});

const cartStorage = createJSONStorage<Record<string, CartItem>>(() => sessionStorage);
const cartHashStorage = createJSONStorage<string>(() => sessionStorage);

export const cartAtom = atomWithStorage<Record<string, CartItem>>('cartAtom', {}, cartStorage);

export const cartQuantityAtom = atom((get) => {
  const cart = get(cartAtom);
  return Object.keys(cart).reduce((sum, key) => sum + cart[key].quantity, 0);
});

export const cartTotalAtom = atom((get) => {
  const cart = get(cartAtom);
  return Object.keys(cart).reduce((sum, key) => sum + cart[key].quantity * cart[key].product.price, 0);
});

export const newItemAddedToCartAtom = atom(false);
export const latestProductIdAtom = atom<string | null>(null);

export const changeCartItemAtom = atom(null, (get, set, update: CartItem) => {
  const cart = get(cartAtom);
  const isNewItem = !cart[update.product.id]; // Check if item doesn't exist in cart
  const latestProductId = get(latestProductIdAtom);

  if (update.quantity === 0) {
    delete cart[update.product.id];
    set(cartAtom, { ...cart });

    if (latestProductId === update.product.id) {
      set(latestProductIdAtom, null);
    }
  } else {
    set(cartAtom, {
      ...cart,
      [update.product.id]: {
        quantity: update.quantity,
        product: update.product,
      },
    });

    if (isNewItem) {
      set(newItemAddedToCartAtom, true);
      set(latestProductIdAtom, update.product.id);
    }
  }
});

export const cartHashAtom = atomWithStorage<string>('cartHash', '', cartHashStorage);

const customerStorage = createJSONStorage<Customer>(() => sessionStorage);

export const customerAtom = atomWithStorage<Customer>(
  'customerAtom',
  {
    firstName: '',
    lastName: '',
    nationalId: '',
    address: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
    delivery: '',
  },
  customerStorage
);

// Enhanced customer validation atom with detailed validation
export const customerValidAtom = atom((get) => {
  const customer = get(customerAtom);
  return (
    validateFirstName(customer.firstName.trim()) &&
    validateNationalId(customer.nationalId.trim()) &&
    validateAddress(customer.address.trim()) &&
    validatePostalCode(customer.postalCode.trim()) &&
    validatePhoneNumber(customer.phoneNumber.trim()) &&
    validateEmail(customer.email.trim()) &&
    customer.delivery.trim().length > 0
  );
});

export const updateUserDetailsAtom = atom(null, (get, set, update: { key: keyof Customer; value: string }) => {
  const prev = get(customerAtom);
  set(customerAtom, { ...prev, [update.key]: update.value });
});

const remoteOrderStorage = createJSONStorage<{ orderId?: string; validUntil?: string }>(() => sessionStorage);
export const remoteOrderAtom = atomWithStorage<{ orderId?: string; validUntil?: string }>(
  'atomWithStorage',
  {},
  remoteOrderStorage
);

export const alertsAtom = atom<
  Record<string, { type: 'info' | 'success' | 'warning' | 'error'; message: string; subMessage?: string }>
>({});

export const pushAlertAtom = atom(
  null,
  (get, set, value: { type: 'info' | 'success' | 'warning' | 'error'; message: string; subMessage?: string }) => {
    const alerts = get(alertsAtom);
    const timestamp = new Date().toISOString();
    alerts[timestamp] = value;

    set(alertsAtom, { ...alerts });
    setTimeout(() => {
      const alerts = get(alertsAtom);
      delete alerts[timestamp];
      set(alertsAtom, { ...alerts });
    }, 5000);
  }
);

export const subscriptionAtom = atom<SellableProduct | null>(null);
